<template>
  <template v-if="isVisible">
    <Modal
      :show="true"
      :title="t('Solicitar Reprocesso')"
      size="small"
      :close-label="t('Cancelar')"
      :save-label="t('Solicitar')"
      :save-disabled-fn="checkCanSave"
      :save-fn="handleSubmit"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <SOReprocessoForm
        v-if="orderItemId !== null"
        ref="formRef"
        :order-item-id="orderItemId"
        :completed-steps="completedSteps"
        @save-success="handleSaveSuccess"
      />
    </Modal>
  </template>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/ui/Modal.vue';
import SOReprocessoForm from './SOReprocessoForm.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface CompletedStep {
  so_work_order_step_id: Id;
  so_work_order_step_name?: string;
  so_work_order_step_production_step_id?: Id;
  [key: string]: unknown;
}

interface FormInstance {
  saveReprocesso: () => Promise<void>;
  canSave: { value: boolean };
}

interface ModalOpenPayload {
  orderItemId?: number | null;
  completedSteps?: CompletedStep[];
}

const { t } = useI18n();

// State
const isVisible = ref<boolean>(false);
const orderItemId = ref<number | null>(null);
const completedSteps = ref<CompletedStep[]>([]);
const formRef = ref<FormInstance | null>(null);

// Botao sempre habilitado — validacao ocorre no handleSubmit
const checkCanSave = (): boolean => false;

// Methods
const handleClose = (): void => {
  log('SOReprocessoModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  orderItemId.value = null;
  completedSteps.value = [];
};

const handleSubmit = async (): Promise<void> => {
  if (!formRef.value) {
    log('SOReprocessoModal', 'handleSubmit', 'Form ref not available');
    return;
  }

  log('SOReprocessoModal', 'handleSubmit', 'Submitting reprocesso form');

  try {
    await formRef.value.saveReprocesso();
    log('SOReprocessoModal', 'handleSubmit', 'Form submitted successfully');
    handleClose();
  } catch (error: unknown) {
    log('SOReprocessoModal', 'handleSubmit', 'Error submitting form', error);
    // Form already shows the specific error toast (e.g. photo required)
  }
};

const handleSaveSuccess = (): void => {
  log('SOReprocessoModal', 'handleSaveSuccess', 'Save success event received');
  eventBus.emit('reprocesso.created');
  handleClose();
};

// EventBus handlers
const handleOpen = (data?: ModalOpenPayload): void => {
  log('SOReprocessoModal', 'handleOpen', 'Opening modal via eventBus', data);
  orderItemId.value = data?.orderItemId ?? null;
  completedSteps.value = data?.completedSteps || [];
  isVisible.value = true;
};

onMounted(() => {
  eventBus.on('modal.soReprocesso.open', handleOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.soReprocesso.open', handleOpen);
});
</script>
